import { useCallback, useEffect, useState } from 'react';
import { fetchMissingBankAccounts, type MissingBankAccount } from '@/api/payment-prep';
import { toMessage } from '@/api/to-message';
import { BankAccountDialog } from '@/components/BankAccountDialog';
import { ROUND_CARD, SMALL_PILL } from '@/payment/payment-styles';

/** 一覧の読み込み状態 */
type ListState =
  | { phase: 'loading' }
  | { phase: 'loaded'; reportName: string; partners: MissingBankAccount[] }
  | { phase: 'failed'; message: string };

/** @graph-connects none */
const YEN = new Intl.NumberFormat('ja-JP');

/**
 * @graph-stack ado-task
 * @graph-domain Finance
 * @graph-business 振込先口座が未登録の取引先（④経費を除く・銀行振込）を金額の大きい順に並べ、行ごとに請求書の読み取り → 確認 → freee 登録を始められるカード。登録した取引先は一覧から外す
 * @graph-connects fetchMissingBankAccounts [calls] 未登録の取引先を読む
 * @graph-connects BankAccountDialog [calls] 読み取りと登録のポップアップ
 */
export function BankAccountSection() {
  const [list, setList] = useState<ListState>({ phase: 'loading' });
  const [selected, setSelected] = useState<MissingBankAccount | null>(null);

  const load = useCallback(async () => {
    setList({ phase: 'loading' });
    try {
      setList({ phase: 'loaded', ...(await fetchMissingBankAccounts()) });
    } catch (cause: unknown) {
      setList({ phase: 'failed', message: toMessage(cause) });
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  return (
    <section className={`${ROUND_CARD} px-5 py-4`} aria-label="振込先口座が未登録の取引先">
      <div className="mb-3 flex flex-wrap items-center gap-2">
        <h2 className="m-0 text-sm font-semibold text-[var(--color-text-primary)]">
          振込先口座が未登録の取引先
        </h2>
        {list.phase === 'loaded' ? (
          <span
            className={
              list.partners.length === 0
                ? 'rounded-full bg-[var(--color-kind-meeting-bg)] px-2 text-xs text-[var(--color-kind-meeting-text)]'
                : 'rounded-full bg-[var(--color-kind-closing-bg)] px-2 text-xs text-[var(--color-kind-closing-text)]'
            }
          >
            {list.partners.length} 社
          </span>
        ) : null}
        <span className="text-xs text-[var(--color-text-muted)]">
          最新の支払管理レポートのうち、④経費を除く銀行振込の取引
        </span>
        <button
          type="button"
          onClick={() => {
            void load();
          }}
          className={`${SMALL_PILL} ml-auto`}
        >
          読み込み直す
        </button>
      </div>

      {list.phase === 'loading' ? (
        <p className="m-0 text-sm text-[var(--color-text-muted)]">確認しています…</p>
      ) : null}
      {list.phase === 'failed' ? (
        <p role="alert" className="m-0 text-sm text-[var(--color-overdue)]">
          読み込めませんでした：{list.message}
        </p>
      ) : null}
      {list.phase === 'loaded' && list.partners.length === 0 ? (
        <p className="m-0 text-sm text-[var(--color-text-secondary)]">未登録の取引先はありません</p>
      ) : null}
      {list.phase === 'loaded' && list.partners.length > 0 ? (
        <ul className="m-0 grid list-none gap-1.5 p-0 md:grid-cols-2">
          {list.partners.map((partner) => (
            <li
              key={partner.partnerId}
              className="flex items-center gap-2 rounded-xl px-3 py-1.5 hover:bg-[var(--color-bg-subtle)]"
            >
              <span className="min-w-0 flex-1 truncate text-sm text-[var(--color-text-primary)]">
                {partner.partnerName}
              </span>
              <span className="shrink-0 text-xs tabular-nums text-[var(--color-text-muted)]">
                ¥{YEN.format(partner.amount)}
                {partner.count > 1 ? `（${String(partner.count)} 件）` : ''}
              </span>
              {partner.receiptIds.length === 0 ? (
                <span className="shrink-0 text-xs text-[var(--color-text-muted)]">証憑なし</span>
              ) : (
                <button
                  type="button"
                  onClick={() => {
                    setSelected(partner);
                  }}
                  className="shrink-0 rounded-full border border-[var(--color-doing-border)] bg-[var(--color-doing-bg)] px-2.5 py-0.5 text-xs font-medium text-[var(--color-doing-text)] hover:opacity-90"
                >
                  請求書から読み取る
                </button>
              )}
            </li>
          ))}
        </ul>
      ) : null}

      {selected === null ? null : (
        <BankAccountDialog
          partner={selected}
          onClose={() => {
            setSelected(null);
          }}
          onRegistered={(partnerId) => {
            setList((current) =>
              current.phase === 'loaded'
                ? {
                    ...current,
                    partners: current.partners.filter((p) => p.partnerId !== partnerId),
                  }
                : current,
            );
          }}
        />
      )}
    </section>
  );
}
